import React, { useMemo } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Box, Small } from "orcs-design-system";
import { Link } from "react-router-dom";
import TeamAvatar from "src/components/TeamAvatar/TeamAvatar";
import GroupTypeBadge from "src/components/GroupTypeBadge";
import { teamDetailsPath } from "src/util/routing";
import { getDirectTeams, getIndirectTeams } from "../utils/teamsUtils";

const TooltipContainer = styled(Box)`
  background-color: ${({ theme }) => theme.colors.white};
  padding: 8px;
  max-width: 300px;
  min-width: 200px;
`;

const SectionTitle = styled(Small)`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.greyDarkest};
  margin-bottom: 4px;
  display: block;
`;

const ScrollableSection = styled(Box)`
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const TeamItemContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
`;

const TeamNameLink = styled(Link)`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.greyDarkest};
  font-weight: 500;
  display: block;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Section = styled(Box)`
  &:not(:last-child) {
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.greyLightest};
  }
`;

const BadgeWrapper = styled(Box)`
  display: inline-block;
  width: fit-content;
`;

/**
 * TeamsListTooltip component displays direct and indirect teams in a tooltip
 * @param {Object} props
 * @param {Array} props.appliedEntities - Array of applied entity objects with relationshipType and group
 * @returns {JSX.Element|null} Tooltip component or null if no teams
 */
const TeamsListTooltip = ({ appliedEntities }) => {
  const directTeams = useMemo(
    () => getDirectTeams(appliedEntities),
    [appliedEntities]
  );
  const indirectTeams = useMemo(
    () => getIndirectTeams(appliedEntities),
    [appliedEntities]
  );

  // If no entities at all, show "No teams linked"
  if (!appliedEntities || appliedEntities.length === 0) {
    return (
      <TooltipContainer>
        <Small color="greyDark">No teams linked</Small>
      </TooltipContainer>
    );
  }

  // If no teams have valid names, show a message
  if (directTeams.length === 0 && indirectTeams.length === 0) {
    return (
      <TooltipContainer>
        <Small color="greyDark">No teams linked</Small>
      </TooltipContainer>
    );
  }

  return (
    <TooltipContainer>
      <ScrollableSection>
        {directTeams.length > 0 && (
          <Section>
            <SectionTitle>Direct Teams</SectionTitle>
            {directTeams.map((team) => {
              return (
                <TeamItemContainer key={team.id}>
                  <TeamAvatar group={team} customSize="24px" />
                  <Box flex="1" minWidth="0">
                    <TeamNameLink to={teamDetailsPath(team, null)}>
                      {team.name}
                    </TeamNameLink>
                    <BadgeWrapper mt="xs">
                      <GroupTypeBadge group={team} />
                    </BadgeWrapper>
                  </Box>
                </TeamItemContainer>
              );
            })}
          </Section>
        )}
        {indirectTeams.length > 0 && (
          <Section>
            <SectionTitle>Indirect Teams</SectionTitle>
            {indirectTeams.map((team) => {
              return (
                <TeamItemContainer key={team.id}>
                  <TeamAvatar group={team} customSize="24px" />
                  <Box flex="1" minWidth="0">
                    <TeamNameLink to={teamDetailsPath(team, null)}>
                      {team.name}
                    </TeamNameLink>
                    <BadgeWrapper mt="xs">
                      <GroupTypeBadge group={team} />
                    </BadgeWrapper>
                  </Box>
                </TeamItemContainer>
              );
            })}
          </Section>
        )}
      </ScrollableSection>
    </TooltipContainer>
  );
};

TeamsListTooltip.propTypes = {
  appliedEntities: PropTypes.arrayOf(
    PropTypes.shape({
      relationshipType: PropTypes.string,
      group: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        kind: PropTypes.string,
        type: PropTypes.string,
      }),
    })
  ),
};

export default TeamsListTooltip;
